import React, { useEffect } from "react";
import Container from "react-bootstrap/Container";
import "./TONearbyPrivacyPolicy.css";

const metaDescription = "Privacy Policy for the TONearby mobile app.";

function TONearbyPrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    let description = document.querySelector('meta[name="description"]');
    const previousDescription = description && description.getAttribute("content");
    const createdDescription = !description;

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    document.title = "TONearby Privacy Policy";
    description.setAttribute("content", metaDescription);

    return () => {
      document.title = previousTitle;

      if (createdDescription) {
        description.remove();
      } else if (previousDescription) {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <main className="tonearby-privacy">
      <Container className="tonearby-privacy__container">
        <article className="tonearby-privacy__content">
          <p className="tonearby-privacy__eyebrow">TONearby</p>
          <h1>TONearby Privacy Policy</h1>
          <p className="tonearby-privacy__date">Effective date: May 6, 2026</p>

          <p>
            TONearby is a mobile app that helps users find nearby Toronto parks,
            community centres, and drop-in recreation activities.
          </p>
          <p>
            This Privacy Policy explains what information TONearby uses, how it
            is used, and what choices you have.
          </p>

          <h2>Information TONearby Uses</h2>

          <h3>Location</h3>
          <p>
            TONearby may ask for access to your device location. Location is
            used to show nearby parks, community centres, distances, map
            results, and activity results around your selected area.
          </p>
          <p>
            TONearby uses location only while you are using the app. TONearby
            does not request background location access.
          </p>
          <p>
            TONearby does not send your precise device location to our own
            servers. Location is used in the app to calculate nearby results.
          </p>
          <p>
            You can use TONearby without granting location permission. If
            location permission is not granted, the app uses Toronto City Hall
            as the default search location until you choose another area.
          </p>

          <h3>Saved Areas</h3>
          <p>
            If you save a Home area, Work area, or picked map area, that
            information is stored locally on your device. It is used only to
            make future searches easier.
          </p>

          <h3>Cached App Data</h3>
          <p>
            TONearby may store recently loaded recreation activity data on your
            device. This helps the app load faster and continue showing recent
            information when your internet connection is unavailable or Toronto
            Open Data is temporarily unavailable.
          </p>
          <p>This cached data does not include personal information about you.</p>

          <h3>Feedback</h3>
          <p>
            If you submit feedback through the app, we may collect the
            information you choose to provide, such as:
          </p>
          <ul>
            <li>feedback category</li>
            <li>message</li>
            <li>name, if provided</li>
            <li>email address, if provided</li>
            <li>phone number, if provided</li>
            <li>
              basic technical information such as app version, platform, device
              model, and operating system version
            </li>
          </ul>
          <p>
            Feedback is used to respond to issues, improve the app, and
            understand reported problems.
          </p>

          <h3>Analytics and Crash Reports</h3>
          <p>
            TONearby uses Firebase services to understand basic app usage and
            diagnose crashes or technical problems.
          </p>
          <p>This may include:</p>
          <ul>
            <li>app opens</li>
            <li>feature usage counts</li>
            <li>selected filter types</li>
            <li>crash reports</li>
            <li>app version</li>
            <li>platform</li>
            <li>device model</li>
            <li>operating system version</li>
            <li>anonymous installation identifier</li>
          </ul>
          <p>
            This information helps improve reliability and understand which
            features are used. TONearby does not use this information to sell
            advertising or create user profiles for advertising.
          </p>

          <h2>Data Sources</h2>
          <p>
            TONearby uses public datasets and APIs, including City of Toronto
            Open Data, to show parks, community centres, and recreation activity
            information.
          </p>
          <p>
            TONearby is not an official City of Toronto app. City datasets may
            change or contain errors.
          </p>

          <h2>Third-Party Services</h2>
          <p>
            TONearby uses third-party services that may process information
            according to their own privacy policies, including:
          </p>
          <ul>
            <li>Google Maps, for map display</li>
            <li>Firebase, for analytics, crash reporting, and feedback storage</li>
            <li>City of Toronto Open Data, for public recreation data</li>
          </ul>
          <p>You can review Google's privacy policy here:</p>
          <p>
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </p>

          <h2>How Information Is Shared</h2>
          <p>TONearby does not sell your personal information.</p>
          <p>TONearby does not share your personal information with advertisers.</p>
          <p>
            Information may be processed by service providers used to operate
            the app, such as Firebase, only for app functionality, analytics,
            crash reporting, feedback handling, and reliability.
          </p>

          <h2>Data Retention</h2>
          <p>
            Saved areas and cached recreation data remain on your device until
            you remove the app or clear app data.
          </p>
          <p>
            Feedback submissions and technical logs may be retained as long as
            reasonably needed to maintain, support, and improve the app.
          </p>

          <h2>Children</h2>
          <p>
            TONearby is not directed specifically to children under 13. The app
            provides general public recreation information.
          </p>

          <h2>Your Choices</h2>
          <p>You can:</p>
          <ul>
            <li>deny or revoke location permission in your device settings</li>
            <li>use the app with Toronto City Hall as the default location</li>
            <li>avoid submitting optional feedback contact information</li>
            <li>
              uninstall the app to remove locally stored app data from your
              device
            </li>
          </ul>

          <h2>Contact</h2>
          <p>
            If you have questions about this Privacy Policy or TONearby's
            privacy practices, contact:
          </p>
          <p>
            <a href="mailto:kordikmail@gmail.com">kordikmail@gmail.com</a>
          </p>
        </article>
      </Container>
    </main>
  );
}

export default TONearbyPrivacyPolicy;
